/**
 * Function to check if a given path exists.
 *
 * @param {object} object
 * @param {string} path
 * @return {*}  {boolean}
 */
const objectPathExists = (object: object, path: string): boolean => {
  const get = (regexp: RegExp) =>
    String.prototype.split
      .call(path, regexp)
      .filter(Boolean)
      .reduce(
        (res, key) => (res !== null && res !== undefined ? res[key] : res),
        object
      );

  const result = get(/[,[\]]+?/) || get(/[,[\].]+?/);
  return result !== undefined;
};

/**
 * Function to set a passed value at the given path.
 *
 * @param {object} object
 * @param {string} path
 * @param {unknown} value
 * @return {*}  {object}
 */
const setObjectPath = (
  object: object,
  path: string,
  value: unknown
): object => {
  if (!objectPathExists(object, path)) {
    if (path.includes('.')) {
      console.error(`Cannot find path in config : "${path}"`);
    }

    return object;
  }

  const matchedPath = path.match(/[^.[\]]+/g);

  matchedPath!.slice(0, -1).reduce((a, c) => a[c], object)[
    matchedPath![matchedPath!.length - 1]
  ] = value;

  return object;
};

/**
 * Function to merge given parsed json config with default config.
 *
 * @param {object} config
 * @param {object} parsedJson
 * @return {*}  {object}
 */
const mergeJsonConfig = (
  config: object | boolean,
  parsedJson: object
): object => {
  if (typeof config === 'object') {
    for (const path in parsedJson) {
      setObjectPath(config, path, parsedJson[path]);
    }

    return config;
  }

  return parsedJson;
};

const mergeConfig = (config, defaultConfig) =>
  Object.assign({}, config, defaultConfig);

/**
 * Function to get a config from a script id.
 *
 * @param {String} configScriptId
 * @param {Object} defaultConfig
 *
 * @return {Object|undefined}
 */
const getConfigFromScript = (configScriptId, defaultConfig = {}) => {
  const configEl = document.getElementById(configScriptId);
  let config = defaultConfig;

  if (!configEl) {
    console.warn(
      `Cannot find config script with element id "${configScriptId}"`
    );

    return {
      config
    };
  }

  const configContent = configEl.innerHTML.trim();

  if (configContent && configContent.length > 0) {
    let parsedConfig;

    try {
      parsedConfig = JSON.parse(configContent);
    } catch (e) {
      console.error(
        `Invalid JSON found in config script with element id "${configScriptId}"`
      );

      return {
        config
      };
    }

    config = mergeJsonConfig(defaultConfig, parsedConfig);
  }

  return {
    config
  };
};

/**
 * Function to get a config from an object.
 *
 * @param {Object} configObject
 * @param {Object} defaultConfig
 *
 * @return {Object|undefined}
 */
const getConfigFromObject = (configObject, defaultConfig = {}) => {
  return {
    config: mergeConfig(defaultConfig, configObject)
  };
};

/**
 * Composable to manage configurations.
 *
 * @param {String} configScriptId
 * @param {Object} configObject
 * @param {Object} defaultConfig
 *
 * @return {Object|undefined}
 */
export const useConfig = (
  configScriptId: string | null = null,
  configObject: Record<string, unknown> | null = null,
  defaultConfig = false
) => {
  if (configScriptId) {
    return getConfigFromScript(configScriptId, defaultConfig);
  }

  if (configObject) {
    return getConfigFromObject(configObject, defaultConfig);
  }

  return {
    config: defaultConfig
  };
};
